import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ReactComponent as Logo } from '../logo.svg';

function MainLayout({ children }) {
  const { user, logout, org } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleClose();
    navigate('/login');
  };

  const handleDashboard = () => {
    handleClose();
    navigate('/memberships');
  };

  const handleLocations = () => {
    handleClose();
    navigate('/locations');
  };

  const handleAccount = () => {
    handleClose();
    navigate('/account');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileMenuClick = (path) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ background: 'none', boxShadow: 'none', borderBottom: 'none', color: org?.primary_color || '#1976d2', }}>
        <Toolbar>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer' 
            }}
            onClick={() => navigate('/')}
          >
            {org ? (
              <img 
                src={org.logo || org.company_logo} 
                alt="Company Logo" 
                style={{ height: 34, marginRight: 24, objectFit: 'contain' }} 
              />
            ) : (
              <Logo style={{ height: 34, marginRight: 24 }} />
            )}
          </Box>

          {user ? (
            <>
              {isMobile ? (
                <>
                  <Box sx={{ flexGrow: 1 }} />
                  <IconButton
                    color="inherit"
                    aria-label="menu"
                    onClick={handleMobileMenuToggle}
                    edge="end"
                    sx={{ mr: 1 }}
                  >
                    <MenuIcon />
                  </IconButton>
                </>
              ) : (
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'right' }}>
                  {!org && (
                    <>
                      <Button
                        color="inherit"
                        onClick={handleDashboard}
                        sx={{ mr: 2 }}
                      >
                        Memberships
                      </Button>
                      <Button
                        color="inherit"
                        onClick={handleLocations}
                      >
                        Discover Locations
                      </Button>
                    </>
                  )}
                </Box>
              )}
              <div>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {user.first_name.charAt(0)}
                  </Avatar>
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleAccount}>Account</MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </div>
            </>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button color="inherit" onClick={() => navigate('/login')}>Login</Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />

      {user && (
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
        >
          <Box
            sx={{ width: 250 }}
            role="presentation"
          >
            <List>
              {!org && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleMobileMenuClick('/memberships')}>
                      <ListItemText primary="Memberships" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleMobileMenuClick('/locations')}>
                      <ListItemText primary="Discover Locations" />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </>
              )}
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleMobileMenuClick('/account')}>
                  <ListItemText primary="Account" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => {
                  handleLogout();
                  handleMobileMenuClose();
                }}>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: (theme) => theme.palette.grey[200]
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            Looking for help? <a href="mailto:support@cinchrewards.com" style={{ color: 'inherit', textDecoration: 'underline' }}>support@cinchrewards.com</a>
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Cinch. All rights reserved. Patents Pending.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default MainLayout;